import { localeRequestHeaders } from './localePreference';
import { apiErrorMessage } from './apiErrorMessages';

const API_PREFIX = '/api/v1';

function applicationApiUrls(path) {
  return [`${API_PREFIX}${path}`];
}

function errorMessageFromPayload(payload, fallback) {
  if (typeof payload?.detail === 'string') return payload.detail;
  if (Array.isArray(payload?.detail)) return payload.detail.map((item) => item?.msg || item?.message).filter(Boolean).join(', ') || fallback;
  if (typeof payload?.message === 'string') return payload.message;
  return fallback;
}

async function apiRequest(path, user, options = {}) {
  const token = await user.getIdToken();
  let lastNetworkError = null;
  const urls = applicationApiUrls(path);

  for (const url of urls) {
    let response;

    try {
      response = await fetch(url, {
        ...options,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...localeRequestHeaders(),
          ...(options.headers || {}),
        },
      });
    } catch {
      lastNetworkError = new Error(apiErrorMessage('applicationNetwork'));
      continue;
    }

    const isJson = (response.headers.get('content-type') || '').toLowerCase().includes('application/json');
    if (!isJson) {
      lastNetworkError = new Error(apiErrorMessage('applicationInvalidResponse', { status: response.status }));
      continue;
    }

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = errorMessageFromPayload(payload, apiErrorMessage('applicationRequest'));
      if (url === urls[0] && urls.length > 1 && response.status >= 500) {
        lastNetworkError = new Error(message);
        continue;
      }
      throw new Error(message);
    }

    return payload;
  }

  throw new Error(lastNetworkError?.message || apiErrorMessage('applicationNetwork'));
}

export function generateApplicationDocument({ user, jobId, documentType, job }) {
  return apiRequest(`/applications/${jobId}/documents`, user, {
    method: 'POST',
    body: JSON.stringify({ documentType, job }),
  });
}

export function exportApplicationDocument({ user, jobId, documentType, content, html, contentFormat = 'text', job }) {
  return apiRequest(`/applications/${jobId}/documents/${documentType}/export`, user, {
    method: 'POST',
    body: JSON.stringify({ content, html, contentFormat, job }),
  });
}

export function generateCvTailoringFeed({ user, jobId, job, tailoredContent = '' }) {
  return apiRequest(`/applications/${jobId}/cv-tailoring/feed`, user, {
    method: 'POST',
    body: JSON.stringify({ job, tailoredContent }),
  });
}

export function submitMatchFeedback({ user, jobId, job, rating, comment = '' }) {
  return apiRequest(`/applications/${jobId}/match-feedback`, user, {
    method: 'POST',
    body: JSON.stringify({ job, rating, comment }),
  });
}

export function submitApplication({
  user,
  jobId,
  job,
  checklist,
  documents = {},
  applicationResponses = [],
  applicationProfile = null,
  source = 'jobseeqr-apply-flow',
}) {
  return apiRequest(`/applications/${jobId}/submit`, user, {
    method: 'POST',
    body: JSON.stringify({
      job,
      checklist,
      documents,
      applicationResponses,
      applicationProfile,
      source,
    }),
  });
}

export function getHiringTeam({ user, jobId }) {
  return apiRequest(`/applications/${jobId}/hiring-team`, user);
}

export function draftOutreachEmails({ user, jobId, job, recipients, outreachLanguage = 'auto' }) {
  return apiRequest(`/applications/${jobId}/outreach/drafts`, user, {
    method: 'POST',
    body: JSON.stringify({ job, recipients, outreachLanguage }),
  });
}

export function saveOutreachTemplate({ user, jobId, job, recipients, drafts, outreachLanguage = 'auto' }) {
  return apiRequest(`/applications/${jobId}/outreach/template`, user, {
    method: 'POST',
    body: JSON.stringify({ job, recipients, drafts, outreachLanguage }),
  });
}

export function startOutreachSequence({ user, jobId, job, recipients, drafts, outreachLanguage = 'auto' }) {
  return apiRequest(`/applications/${jobId}/outreach/start`, user, {
    method: 'POST',
    body: JSON.stringify({ job, recipients, drafts, outreachLanguage }),
  });
}

export function updateOutreachSequence({ user, sequenceId, status }) {
  return apiRequest(`/applications/outreach/sequences/${sequenceId}`, user, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export function updateOutreachRecipient({ user, sequenceId, recipientId, status }) {
  return apiRequest(`/applications/outreach/sequences/${sequenceId}/recipients/${recipientId}`, user, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export function getNetworkingPeople({ user, jobId }) {
  return apiRequest(`/applications/${jobId}/networking-people`, user);
}

export function getJobConnections({ user, jobId }) {
  return apiRequest(`/applications/${jobId}/connections`, user);
}

export function getSourceCvMarkdown({ user, jobId }) {
  return apiRequest(`/applications/${jobId}/source-cv`, user);
}

export function draftConnectionMessage({ user, jobId, personId }) {
  return apiRequest(`/applications/${jobId}/connections/message`, user, {
    method: 'POST', body: JSON.stringify({ personId }),
  });
}

export function markApplicationApplied({ user, jobId }) {
  return apiRequest(`/applications/${jobId}/mark-applied`, user, { method: 'POST' });
}
